import { useState } from "react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { PACKAGES, COMPARISON } from "@/data/content";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { scrollToHash } from "@/components/Navbar";
import { trackEvent, EVENTS } from "@/lib/analytics";
import { cn } from "@/lib/utils";

export const Packages = () => {
    const [open, setOpen] = useState(null);

    return (
        <section id="packages" data-testid="packages-section" className="blueprint noise relative overflow-hidden py-24 lg:py-36">
            <div className="pointer-events-none absolute -right-48 top-24 h-[480px] w-[480px] rounded-full bg-flame/10 blur-[150px]" />
            <div className="relative z-10 mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10">
                <SectionHeading
                    id="packages-heading"
                    dark
                    overline="Construction Packages"
                    title={<>Five packages. <span className="text-flame">One standard of clarity.</span></>}
                    description="Confirmed OMSIVO construction rates with published scope — compare structure, flooring, kitchen, bathroom and doors & windows across all five packages."
                />

                <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
                    {PACKAGES.map((p, i) => (
                        <Reveal key={p.name} delay={0.06 * i} className="h-full">
                            <div
                                className={cn(
                                    "lift-card relative flex h-full flex-col overflow-hidden rounded-2xl border p-7",
                                    i === 4
                                        ? "border-flame/60 bg-gradient-to-b from-flame/15 to-white/[0.03]"
                                        : "border-white/12 bg-white/[0.04]"
                                )}
                                data-testid={`package-card-${p.tier}`}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-flame">{p.tier}</span>
                                    {i === 4 && (
                                        <span className="rounded-full bg-flame px-2.5 py-1 font-mono text-[9px] font-semibold uppercase tracking-widest text-white">Flagship</span>
                                    )}
                                </div>
                                <h3 className="mt-5 font-display text-xl font-black uppercase tracking-[0.06em] text-white">{p.name}</h3>
                                <p className="mt-4">
                                    <span className="font-display text-4xl font-black tracking-tight text-white">₹{p.rate.toLocaleString("en-IN")}</span>
                                    <span className="ml-1 font-mono text-[10px] uppercase tracking-widest text-white/50">/Sq.Ft.*</span>
                                </p>
                                <div className="mt-4 border-t border-white/10 pt-4">
                                    <p className="font-mono text-[9px] font-semibold uppercase tracking-[0.22em] text-flame">{p.tag}</p>
                                    <p className="mt-2 text-[12px] leading-relaxed text-white/55">
                                        <span className="font-semibold uppercase tracking-wider text-white/75">Best for: </span>{p.bestFor}
                                    </p>
                                </div>
                                <div className="mt-auto flex flex-col gap-2.5 pt-6">
                                    <button
                                        onClick={() => { setOpen(p); trackEvent(EVENTS.packageViewed, { package: p.name }); }}
                                        data-testid={`package-scope-btn-${p.tier}`}
                                        className="rounded-full border border-white/20 py-2.5 font-display text-[11px] font-bold uppercase tracking-[0.12em] text-white transition-colors hover:border-flame hover:text-flame"
                                    >
                                        View Complete Scope
                                    </button>
                                    <button
                                        onClick={() => { trackEvent(EVENTS.packageSelected, { package: p.name }); scrollToHash("#contact"); }}
                                        data-testid={`package-consult-btn-${p.tier}`}
                                        className="rounded-full bg-flame py-2.5 font-display text-[11px] font-bold uppercase tracking-[0.12em] text-white transition-all hover:-translate-y-0.5 hover:bg-flame-dark"
                                    >
                                        Request Consultation
                                    </button>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.2}>
                    <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">
                        * Confirmed OMSIVO package rates. Final pricing depends on confirmed scope, design and site conditions.
                    </p>
                </Reveal>
            </div>

            <Dialog open={!!open} onOpenChange={() => setOpen(null)}>
                <DialogContent className="max-w-lg border-white/10 bg-omsivo-deep text-white" data-testid="package-scope-modal">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl font-black uppercase tracking-wide">
                            {open?.name} <span className="text-flame">₹{open?.rate.toLocaleString("en-IN")}/Sq.Ft.*</span>
                        </DialogTitle>
                        <DialogDescription className="text-white/60">
                            Complete package scope as confirmed by OMSIVO.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="mt-2 max-h-[52vh] space-y-5 overflow-y-auto pr-1" data-lenis-prevent>
                        {COMPARISON.map((cat) => (
                            <div key={cat.category}>
                                <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.24em] text-flame">{cat.category}</p>
                                <div className="mt-2 rounded-xl border border-white/10">
                                    {cat.rows.map((r) => (
                                        <div key={r.spec} className="flex items-start justify-between gap-4 border-b border-white/10 px-4 py-2.5 last:border-0">
                                            <span className="text-xs leading-snug text-white/55">{r.spec}</span>
                                            <span className="text-right text-xs font-semibold leading-snug text-white">{open && r.values[Number(open.tier) - 1]}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                    <p className="mt-4 text-xs leading-relaxed text-white/55">
                        Final scope and specifications are confirmed as per the selected package, project requirements and approved OMSIVO scope of work. Specifications may vary based on project design, site conditions and mutually agreed scope.
                    </p>
                    <button
                        onClick={() => { setOpen(null); scrollToHash("#contact"); }}
                        data-testid="package-modal-consult-btn"
                        className="mt-2 w-full rounded-full bg-flame py-3.5 font-display text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors hover:bg-flame-dark"
                    >
                        Request Consultation
                    </button>
                </DialogContent>
            </Dialog>
        </section>
    );
};
